<template>
  <div>
    <div v-if="displayLoading">
      <Loading />
    </div>

    <div class="page">
      <div v-if="getModuleConfig('email_verify_page.step_section.enable_step_section')" class="p-3">
        <SharedStep :steps="getModuleConfig('email_verify_page.step_section.steps')" />
      </div>

      <div class="page__title">
        {{ getModuleConfig("email_verify_page.form_title") || trans('email_verify_title', 'Email 驗證') }}
      </div>
      <div
        v-if="getModuleConfig('email_verify_page.form_desc')"
        class="page__desc"
        v-html="getModuleConfig('email_verify_page.form_desc')"
      ></div>

      <div class="page__desc__text">
        {{ trans('email_code_send_to', 'Email 驗證碼已發送至') }}
        <b class="google-sans-code">{{ maskedEmail }}</b>
        <div v-if="counter > 0">
          <div v-html="counterMessage"></div>
        </div>
        <div v-else>
          {{ trans('expired_email_code', 'Email 驗證碼已過期，請重新取得驗證碼。') }}
        </div>
      </div>

      <div class="code-input">
        <CodeInput
          ref="codeInput"
          :key="codeInputKey"
          :loading="false"
          :fields="verifyCodeLength"
          :fieldHeight="80"
          class="google-sans-code"
          :class="{ invalid: !isValid }"
          @change="onChange"
          @complete="onChange"
        />
        <div v-if="!isValid" class="form__error-msg">
          {{ errorMessage || trans('wrong_email_code', '驗證碼有誤，請再次確認') }}
        </div>
      </div>

      <div v-if="message" class="email-validate__message">
        {{ message }}
      </div>

      <div class="page__bottom">
        {{ trans('not_receive', '沒收到嗎？') }}
        <span
          @click="handleResend"
        >
          {{ resendText }}
        </span>
      </div>
      <div class="page__bottom">
        {{ trans('wrong_email', 'Email 錯誤？') }}
        <span @click="handleReturn">{{ trans('refill_data', '重新填寫資料') }}</span>
      </div>
    </div>

    <div class="page__button">
      <SharedButton
        class="s-btn-bg-primary"
        type="button"
        :disabled="submitIsDisabled"
        @click="handleSubmit"
      >
        {{ getModuleConfig("email_verify_page.button_text") || trans('submit', '送出') }}
      </SharedButton>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Page/Liff/Shared/Loading";
import CodeInput from "@/components/CodeInput";
import registerApi from "@/apis/liff/v2/register";
import SharedButton from "@/components/Page/Liff/Shared/Button";
import SharedStep from "@/components/Page/Liff/Shared/Steps";
import registerMixin from "@/mixins/liff/register";
import { mapActions } from "vuex";

export default {
  name: "RegisterEmailValidate",
  mixins: [registerMixin],
  components: {
    CodeInput,
    Loading,
    SharedButton,
    SharedStep,
  },

  data() {
    return {
      displayLoading: false,
      verificationCode: "",
      counter: 0,
      emailVerifyContext: {},
      timer: null,
      isValid: true,
      message: null,
      errorMessage: null,
      codeInputKey: 0,
    };
  },

  computed: {
    email() {
      return this.$route.query.email || this.emailVerifyContext.email || this.$store.state.liffAuth.user.email || "";
    },
    maskedEmail() {
      if (!this.email || !this.email.includes("@")) {
        return this.email || this.trans('your_email', '您的 Email');
      }

      const [name, domain] = this.email.split("@");
      const prefix = name.substring(0, 2);
      return `${prefix}${"*".repeat(Math.max(name.length - 2, 3))}@${domain}`;
    },
    verifyCodeLength() {
      const length = parseInt(this.getModuleConfig("email_verify_page.verify_code_length"), 10);
      return !isNaN(length) && length > 0 ? length : 6;
    },
    timeout() {
      return {
        minute: Math.floor(this.counter / 60),
        second: Math.floor(this.counter % 60),
      };
    },
    timeString() {
      let timeString = "";
      if (this.timeout.minute) timeString += `${this.timeout.minute} ${this.trans('minute', '分')} `;
      if (this.timeout.second) timeString += `${this.timeout.second} ${this.trans('second', '秒')}`;
      return timeString;
    },
    counterMessage() {
      const defaultTemplate = [
        '驗證碼將',
        this.timeout.minute > 0 ? ` <b>{minute}</b> 分` : '',
        this.timeout.second > 0 ? ` <b>{second}</b> 秒` : '',
        '後失效'
      ].join('');

      return this.trans('email_code_expire_message', defaultTemplate, {
        minute: this.timeout.minute,
        second: this.timeout.second,
      });
    },
    resendText() {
      return this.trans('send_email_code_again', '重新發送驗證碼');
    },
    submitIsDisabled() {
      return this.displayLoading || this.verificationCode.length !== this.verifyCodeLength;
    },
  },

  mounted() {
    this.emailVerifyContext = this.getEmailVerifyContext();
    this.initCountersFromQuery();
    this.startTimer();
  },

  beforeDestroy() {
    this.clearTimer();
  },

  methods: {
    ...mapActions("liffAuth", ["fetchMe", "setBindingErrorMessage"]),

    initCountersFromQuery() {
      const query = {
        ...this.emailVerifyContext,
        ...this.$route.query,
      };
      const now = Math.floor(Date.now() / 1000);
      const expiredAt = parseInt(query.expired_at, 10);

      if (!isNaN(expiredAt) && expiredAt > now) {
        this.counter = expiredAt - now + 1;
      }
    },
    onChange(value) {
      this.verificationCode = value;
      this.isValid = true;
      this.errorMessage = null;
    },
    async handleSubmit() {
      if (this.submitIsDisabled) {
        this.isValid = false;
        return;
      }

      this.displayLoading = true;

      try {
        const response = await registerApi.verifyEmailOtp({
          from: this.$route.query.from,
          otp: this.verificationCode,
        });

        await this.fetchMe();

        if (response.data.status === "success" && response.data.next) {
          await this.goNext(response.data.next, { from: this.$route.query.from });
          return;
        }

        if (response.data.status === "error" && response.data.next) {
          this.setBindingErrorMessage(response.data.reason || response.data.message);
          await this.goNext(response.data.next);
          return;
        }

        await this.goNext("success_page");
      } catch (error) {
        this.isValid = false;
        this.errorMessage = error.response?.data?.message || this.trans('verify_email_failed', 'Email 驗證失敗，請重新確認');
      } finally {
        this.displayLoading = false;
      }
    },
    async handleResend() {
      if (this.displayLoading) {
        return;
      }

      this.displayLoading = true;
      this.isValid = true;
      this.message = null;
      this.errorMessage = null;
      this.clearCodeInput();

      try {
        const response = await registerApi.resendEmailOtp({
          from: this.$route.query.from,
        });

        const responseData = this.resolveResponseData(response.data);

        this.message = responseData.message || this.trans('sent_email', 'Email 驗證碼已寄出');
        this.counter = this.resolveRemainingSecondsFromExpiredAt(responseData.expired_at, this.counter);
        this.restartTimer();
      } catch (error) {
        this.errorMessage = error.response?.data?.message || this.trans('email_resend_failed', 'Email 驗證碼發送失敗');
        this.$swal(this.trans('please_wait', '請稍候'), this.errorMessage, 'error');
      } finally {
        this.displayLoading = false;
      }
    },
    handleReturn() {
      const routeName = this.$route.query.from === 'verify_form'
        ? "LiffRegisterVerifyForm"
        : "LiffRegisterMember";

      this.$router.push({
        name: routeName,
        params: {
          orgCode: this.$route.params.orgCode,
        },
        query: this.$route.query.from ? { from: this.$route.query.from } : {},
      });
    },
    resolveResponseData(responseData) {
      return {
        ...responseData,
        ...(responseData?.data || {}),
      };
    },
    resolveRemainingSecondsFromExpiredAt(expiredAt, fallback) {
      const parsedExpiredAt = parseInt(expiredAt, 10);

      if (isNaN(parsedExpiredAt) || parsedExpiredAt <= 0) {
        return fallback;
      }

      const now = Math.floor(Date.now() / 1000);
      return Math.max(0, parsedExpiredAt - now + 1);
    },
    startTimer() {
      this.clearTimer();

      if (this.counter <= 0) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.counter--;

        if (this.counter > 0) {
          return;
        }

        this.clearTimer();
      }, 1000);
    },
    clearTimer() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    },
    restartTimer() {
      this.startTimer();
    },
    clearCodeInput() {
      this.verificationCode = "";
      this.codeInputKey++;
      this.$nextTick(() => {
        this.$refs.codeInput?.$el?.querySelector("input")?.focus();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.form__error-msg {
  color: #fe0000;
  font-size: 12px;
  margin-top: 8px;
  text-align: center;
}

.code-input {
  margin-bottom: 32px;
  margin-top: 20px;

  ::v-deep .react-code-input-container {
    margin: auto;
    text-align: center;

    &.invalid input {
      border: 1px solid #fe0000;
    }
  }

  ::v-deep input {
    border: 1px solid #636366 !important;
    border-radius: 10px !important;
    color: var(--primary-color) !important;
    font-family: PingFang TC;
    font-size: 50px;
    font-weight: 600;

    &:focus {
      border: 1px solid var(--liff-primary_text_color);
      caret-color: var(--liff-primary_text_color);
    }
  }
}

.page__bottom {
  color: var(--liff-primary_text_color);
  font-size: 14px;
  line-height: 22px;
  text-align: center;

  span {
    color: var(--primary-color) !important;
    cursor: pointer;
    text-decoration: underline;

    &.disabled {
      color: var(--liff-secondary_text_color) !important;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
}

.page__desc__text {
  color: var(--liff-primary_text_color) !important;
  text-align: center;

  b {
    color: var(--primary-color) !important;
  }
}

.page__desc {
  color: var(--liff-secondary_text_color);
}

.email-validate__message {
  color: var(--liff-primary_text_color);
  font-size: 14px;
  margin-bottom: 16px;
  text-align: center;
}
</style>
